import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-segmented-filter>` says about itself.
 *
 * This piece and TabStrip look almost identical on a screen — a short horizontal row of small
 * uppercase words, one of them lit. They are not the same thing and putting the wrong one in is the
 * mistake this file exists to prevent, so the distinction is stated here as well as in `insteadUse`:
 *
 *   SegmentedFilter narrows a SET. The records on the surface are the same records; fewer of them
 *   are shown. It sits on the toolbar of the thing it filters, it is a `role="group"` of
 *   `aria-pressed` buttons, and it draws as a pill-shaped inset control with the active segment
 *   filled solid in the accent.
 *
 *   TabStrip swaps the SUBJECT. Different content occupies the region. It is a real ARIA tablist
 *   with an underline indicator and it can own panels.
 *
 * A person operating a screen reader gets told which of the two they have — "pressed" is a filter,
 * "selected tab" is a facet — so getting this wrong is not only a visual matter.
 *
 * A smaller finding, recorded because a reader would otherwise have to work it out: the event is
 * named plain `change`, and it BUBBLES and is COMPOSED. A `change` listener on a surrounding form
 * will hear it and cannot tell it apart from a native one by name alone.
 */
const docs: ComponentDocs = {
  aliases: ['segmented control', 'toggle group', 'filter pills', 'quick filters', 'button group', 'chips'],

  answers: [
    'let somebody narrow a list to a few named subsets',
    'switch between All, Mine and Active on a list',
    'offer three or four mutually exclusive choices in one row',
    'filter what a table shows without opening a filter panel',
  ],

  // The toolbar of a surface that shows a set of records. It is not a record-page control: what
  // narrows a set belongs where the set is.
  placements: ['homePage', 'appPage'],

  inputs: [
    {
      name: 'options',
      // No attribute: a list of segments cannot be written in one. Plain strings are accepted as a
      // shorthand and are expanded into { value, label } pairs by the setter.
      type: 'json',
      description:
        'The segments, as { value, label } objects or as plain strings, in the order they are drawn. ' +
        'A string becomes both the value and the label.',
    },
    {
      name: 'value',
      attribute: 'value',
      property: true,
      type: 'text',
      description:
        'Which segment is in force. Reflected as an attribute, so it can be read back off the element. ' +
        'When the page sets none, the FIRST option becomes the value as soon as the segments are drawn.',
    },
    {
      // CAOS-1572 — declared, rather than left to `aria-label`, because a stored placement carries a
      // component's own inputs and nothing else: an element attribute nobody declares is unsayable
      // on a page assembled from metadata, and this control's meaning is exactly what its name says.
      name: 'label',
      attribute: 'label',
      type: 'text',
      description:
        'The accessible name of the control — WHAT is being filtered, in the words a screen reader ' +
        'reads out: Scope, Period, Owner. Follows the attribute whenever it changes. Without one the ' +
        'group is announced as "Filter", which distinguishes nothing on a toolbar carrying two.',
    },
  ],

  events: [
    {
      name: 'change',
      when:
        'A segment is chosen and it was not already in force — by pointer, or by an arrow key, which ' +
        'moves and selects in one gesture. Re-choosing the segment already in force raises nothing.',
      carries: '{ value } — the value of the segment now in force.',
    },
  ],

  // Genuinely none. `<button>` and `<option>` children ARE read on connect as a declarative source
  // for the segments, but they are READ and then replaced by drawn segments in the shadow tree —
  // they are never projected, so they are not a slot and nothing put inside them survives.
  // Nothing to read back that cannot also be set. `value` can be read off the element, but a page can
  // set it too, so it is documented as an input rather than here.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-color-accent-fill', controls: 'The fill and border of the segment in force.' },
    { token: '--caos-color-accent-contrast', controls: 'The ink of the segment in force. White by default; a brand with a pale accent sets a darker ink here.' },
    { token: '--caos-color-surface-2', controls: 'The inset track the segments sit in.' },
    { token: '--caos-color-border', controls: 'The outline of the track.' },
    { token: '--caos-color-text-muted', controls: 'The ink of the segments that are not in force.' },
    { token: '--caos-color-text', controls: 'The ink of a hovered segment.' },
    { token: '--caos-font-mono', controls: 'The typeface of the segment labels.' },
    { token: '--caos-focus-ring', controls: 'The ring drawn on a keyboard-focused segment.' },
    { token: '--caos-radius-sm', controls: 'The corner radius of each segment.' },
  ],

  parts: [
    { part: 'group', is: 'the group of segments' },
    { part: 'segment', is: 'one segment' },
  ],

  methods: [],

  placing:
    'On the toolbar of the surface it filters, beside the search box and above the table or cards it ' +
    'narrows — never as a heading and never at the top of a record. It is inline-flex, so it hugs its ' +
    'segments and several controls sit on one line; past the width available the track scrolls ' +
    'sideways rather than wrapping, so keep the set short. Placing it is only half the job: it reports ' +
    'the change and the surface re-queries.',

  whenToUse:
    'Two to five mutually exclusive filters that a person switches between constantly and should be ' +
    'able to read all of at once. If they would not all fit on one line, this is the wrong control.',

  insteadUse: [
    {
      name: 'TabStrip',
      when:
        'The row swaps the SUBJECT rather than narrowing a set — Details, Related, History on one ' +
        'record. That is a real ARIA tablist, it can own the panels beneath it, and it is announced as ' +
        'tabs. This one is announced as pressed buttons and owns nothing below it.',
    },
    {
      name: 'PicklistField',
      when:
        'There are more choices than fit on a line, or the choice is a VALUE ON THE RECORD being saved ' +
        'rather than a view of a list.',
    },
    {
      name: 'Button',
      when: 'Pressing it DOES something rather than changing what is shown.',
    },
    {
      name: 'WorkspaceTabs',
      when:
        'You are moving between objects in an app rather than filtering one list. That belongs to the ' +
        'workspace frame and is not placed on a page at all.',
    },
  ],

  accessibility:
    'The segments are real buttons inside a `role="group"`, each carrying `aria-pressed`, so the state ' +
    'is announced as pressed rather than as a selected tab — which is the truthful reading of a filter. ' +
    'Keyboard is a roving tabindex: one Tab stop for the whole control, and Arrow Left/Right/Up/Down, ' +
    'Home and End move focus AND change the filter in one gesture. Focus rings, tab order and the ' +
    'default group name ("Filter") are handled. Left to the author: set `label` to say what is being ' +
    'filtered — a control announced as "Filter" on a page with two of them says nothing. `label` is an ' +
    'input rather than an `aria-label` attribute so that a page assembled from stored metadata can set ' +
    'it; `aria-label` written straight onto the element is still read, and `label` wins where both are ' +
    'given. Because arrow keys select as they move, avoid this control where each change is expensive.',

  examples: [
    {
      key: 'scope',
      label: 'Scoping a list',
      summary: 'Three named subsets with one in force — how a list toolbar narrows what a table shows.',
      placement: {
        component: 'caos-segmented-filter',
        inputs: {
          label: 'Scope',
          value: 'active',
          options: [
            { value: 'active', label: 'Active' },
            { value: 'mine', label: 'Mine' },
            { value: 'all', label: 'All' },
          ],
        },
      },
    },
    {
      key: 'shorthand',
      label: 'Plain strings, no value set',
      summary:
        'Segments given as bare strings and no value supplied — the labels become their own values and ' +
        'the first segment takes force on its own, so a page that sets nothing is still in a valid state.',
      placement: {
        component: 'caos-segmented-filter',
        inputs: { label: 'Period', options: ['Week', 'Month', 'Quarter', 'Year'] },
      },
    },
  ],
};

export default docs;
